import { useCallback, useEffect, useRef, useState } from 'react'
import type { ClientUser } from '@numeo/types-core'
import { useAuth } from 'hooks/auth'
import { fetchDriverRoster } from 'services/teamGroupsApi'
import { createScopedRequestCache, ScopedRequestSuperseded } from 'utils/scoped-request-cache'
import { getErrorStatus } from 'utils/get-user-error-message'
import { reportError } from 'utils/errorReporter'

/**
 * The roster, fetched ONCE for everybody who asks at the same time.
 *
 * Three screens need this list and the endpoint behind it is not cheap — it resolves the Groups scope and
 * the Box Truck recruiter reach before it answers — so three components mounting together used to mean
 * three of those. The sharing, the reuse window, the single retry for the momentary 503 and the
 * identity-scoping are `createScopedRequestCache`'s, not this file's: the next list that needs a
 * server-scoped read should reuse that rather than grow a second copy of this.
 */
const rosterCache = createScopedRequestCache<ClientUser[]>(fetchDriverRoster, {
    // Reported HERE rather than in the hook's own `.catch`: the promise is shared, so reporting per waiter
    // turned one outage into one report per mounted screen.
    //
    // As an OUTAGE, not as a product event. What happens on screen is a list that shrinks or empties, which
    // reads to the operator as an answer rather than a failure, so it has to be visible somewhere — and
    // "something is broken" is the error channel's question. (Amplitude events in the admin panel are a
    // product decision and not this change's to make.)
    onFailure: (error) =>
        reportError({
            code: 'BOX_TRUCK_DRIVER_ROSTER_UNAVAILABLE',
            errorMessage: 'Driver roster could not be loaded; the Box Truck driver lists and pickers are empty for this operator',
            severity: 'warning',
            feature: 'box-truck-profiles',
            metadata: { status: getErrorStatus(error) },
        }),
})

/** The roster for one identity, shared with anyone else asking for it right now. */
export const loadDriverRoster = (scope: string): Promise<ClientUser[]> => rosterCache.load(scope)

/**
 * Drop the shared answer, so the next read goes to the server.
 *
 * Drops the IN-FLIGHT request with it: a request that started before a save answers with the roster from
 * before the save, and caching that made a create, edit or termination look lost on every screen holding
 * the list.
 */
export const invalidateDriverRoster = (): void => rosterCache.invalidate()

/** The last `refreshToken` an invalidation was already made for. A fresh object, so the first one counts. */
let invalidatedFor: unknown = Symbol('no refresh token seen')

/**
 * Invalidate ONCE for a given token, however many screens are holding it.
 *
 * Every mounted `useDriverRoster` sees the same Redux `users` array change at the same moment. Each
 * invalidating in turn would kill the request the one before it had just started, so a single save would
 * cost one server round trip per mounted screen — the opposite of what the shared cache is for.
 */
const invalidateOnceFor = (token: unknown): void => {
    if (token === invalidatedFor) return
    invalidatedFor = token
    invalidateDriverRoster()
}

/**
 * Whose roster this is: the tenant and the user the server answered for.
 *
 * Passed to every read, so a cached answer can never outlive the identity that earned it — signing out
 * navigates without a reload, and the company switch happens in-tab.
 */
export const useDriverRosterScope = (): string => {
    const { application, user } = useAuth()
    return `${application?.id ?? ''}:${user?.id ?? ''}`
}

interface DriverRosterState {
    drivers: ClientUser[]
    /** The server has answered. Separate from an empty list: "not answered yet" and "nobody" are different. */
    loaded: boolean
    /** It answered with an error, after the retry. A caller that narrows on this list must not widen here. */
    failed: boolean
    reload: () => void
}

/**
 * The drivers the SERVER says this caller may work with.
 *
 * `application.users` is the whole tenant, and every Box Truck write that names a driver refuses one
 * outside the caller's reach — so a picker built on Redux offers people the operator cannot choose and
 * answers "Driver not found" about somebody plainly on the list. This endpoint applies the same scopes
 * the writes do (Groups, and the Box Truck recruiter reach), and reads from `applications.users[]`, so
 * it also carries drivers the derived `drivers` collection has not caught up with.
 *
 * `refreshToken` is any value that changes when this screen saved something — Redux's `application.users`
 * array, typically. Changing it drops the shared answer, so the list a save just changed is re-read here
 * AND everywhere else holding it.
 */
export const useDriverRoster = (refreshToken?: unknown): DriverRosterState => {
    const scope = useDriverRosterScope()
    const [state, setState] = useState<{ drivers: ClientUser[]; loaded: boolean; failed: boolean }>({ drivers: [], loaded: false, failed: false })
    const [reloadKey, setReloadKey] = useState(0)
    const firstRun = useRef(true)

    const [answeredFor, setAnsweredFor] = useState(scope)
    if (answeredFor !== scope) {
        setAnsweredFor(scope)
        setState({ drivers: [], loaded: false, failed: false })
    }

    // The rows belong to the identity that asked for them, so they are dropped the moment it changes —
    // during the render that notices, not in an effect afterwards. A company switch or a re-login happens
    // without remounting these screens, and `loaded` is what the callers gate on: holding the previous
    // tenant's drivers while the new request is in the air shows their names and ids, and holding them
    // when that request FAILS shows them for good.

    const reload = useCallback(() => {
        invalidateDriverRoster()
        setReloadKey((key) => key + 1)
    }, [])

    useEffect(() => {
        // A changed token means a write landed; the first run is just the mount, which the cache may serve.
        if (firstRun.current) firstRun.current = false
        else invalidateOnceFor(refreshToken)
        let alive = true
        loadDriverRoster(scope)
            .then((rows) => {
                if (alive) setState({ drivers: rows, loaded: true, failed: false })
            })
            // Refusing rather than guessing: the endpoint answers 503 when the rules cannot be read, and
            // widening to the tenant list would put back exactly the drivers this exists to withhold.
            .catch((error) => {
                // Reported once per request by the cache's `onFailure`, not here: this runs per waiter.
                // A superseded read is not a failure — the identity changed, or a save invalidated the
                // read it had already started — so it is neither logged as one nor shown as one.
                if (error instanceof ScopedRequestSuperseded) return
                console.error('[box-truck] driver roster unavailable', error)
                if (alive) setState((prev) => ({ drivers: prev.drivers, loaded: prev.loaded, failed: true }))
            })
        return () => {
            alive = false
        }
    }, [refreshToken, reloadKey, scope])

    return { drivers: state.drivers, loaded: state.loaded, failed: state.failed, reload }
}
